import React, {useState} from 'react';
import PropTypes from 'prop-types';
import './ReasoningStatus.css';

/**
 * StepBar - renders a single collapsible bar for one reasoning step.
 */
const StepBar = ({step, statusClass}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const icon = statusClass === 'active' ? '⟳' : statusClass === 'completed' ? '✓' : '○';
    const hasDetails = step.description || step.functionCall || step.result || step.reasoning || step.duration;

    const toggle = () => {
        if (hasDetails) {
            setIsExpanded(prev => !prev);
        }
    };

    return (
        <div className={`reasoning-status-box ${statusClass}`}>
            <div
                className="reasoning-status-header"
                onClick={toggle}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggle();
                    }
                }}
                role={hasDetails ? 'button' : undefined}
                tabIndex={hasDetails ? 0 : undefined}
                style={hasDetails ? undefined : {cursor: 'default'}}
            >
                <div className="reasoning-status-left">
                    <span className={`reasoning-status-icon ${statusClass}`}>
                        {icon}
                    </span>
                    <span className="reasoning-status-title">
                        {step.title}
                    </span>
                </div>
                {hasDetails && (
                    <span className={`reasoning-status-arrow ${isExpanded ? 'expanded' : ''}`}>
                        {'▼'}
                    </span>
                )}
            </div>

            {isExpanded && hasDetails && (
                <div className="reasoning-status-details">
                    {step.description && (
                        <p className="timeline-description">{step.description}</p>
                    )}

                    {step.functionCall && (
                        <div className="timeline-function-call">
                            <div className="function-call-header">{'Tool Call'}</div>
                            <div className="function-call-body">
                                <code className="function-name">{step.functionCall.name}</code>
                                {step.functionCall.args && Object.keys(step.functionCall.args).length > 0 && (
                                    <div className="function-parameters">
                                        <strong>{'Parameters:'}</strong>
                                        <pre className="parameter-json">
                                            {JSON.stringify(step.functionCall.args, null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {step.result && (
                        <div className="timeline-result">
                            <div className="result-header">{'Result'}</div>
                            <div className="result-body">
                                {typeof step.result === 'string' ? (
                                    <p className="result-text">{step.result}</p>
                                ) : (
                                    <pre className="result-json">
                                        {JSON.stringify(step.result, null, 2)}
                                    </pre>
                                )}
                            </div>
                        </div>
                    )}

                    {step.reasoning && (
                        <div className="timeline-reasoning">
                            <div className="reasoning-label">{'Reasoning'}</div>
                            <p className="reasoning-text">{step.reasoning}</p>
                        </div>
                    )}

                    {step.duration && (
                        <div className="timeline-duration">
                            <small>{'Duration: '}{step.duration}{'ms'}</small>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

StepBar.propTypes = {
    statusClass: PropTypes.oneOf(['active', 'completed', 'pending']).isRequired,
    step: PropTypes.shape({
        description: PropTypes.string,
        duration: PropTypes.number,
        functionCall: PropTypes.shape({
            args: PropTypes.object,
            name: PropTypes.string.isRequired
        }),
        reasoning: PropTypes.string,
        result: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        title: PropTypes.string.isRequired
    }).isRequired
};

/**
 * ReasoningStatus Component
 *
 * Renders one collapsible bar per reasoning step (only steps that have started).
 * Past steps show a checkmark; the current step shows a spinner while active.
 */
const ReasoningStatus = ({reasoningSteps = [], currentStep, isActive = true}) => {
    if (!reasoningSteps || reasoningSteps.length === 0) {
        return null;
    }

    // Only render steps that have been reached (index <= currentStep)
    const visibleSteps = reasoningSteps.slice(0, currentStep + 1);

    return (
        <div className="reasoning-status-list">
            {visibleSteps.map((step, index) => {
                const isCurrentStep = index === currentStep;
                const statusClass = isCurrentStep
                    ? (isActive ? 'active' : 'completed')
                    : 'completed';

                return <StepBar key={index} step={step} statusClass={statusClass} />;
            })}
        </div>
    );
};

ReasoningStatus.propTypes = {
    currentStep: PropTypes.number.isRequired,
    isActive: PropTypes.bool,
    reasoningSteps: PropTypes.arrayOf(PropTypes.shape({
        description: PropTypes.string,
        duration: PropTypes.number,
        functionCall: PropTypes.shape({
            args: PropTypes.object,
            name: PropTypes.string.isRequired
        }),
        reasoning: PropTypes.string,
        result: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.object
        ]),
        title: PropTypes.string.isRequired
    }))
};

export default ReasoningStatus;
